import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import { api } from "@/lib/api";
import { canSeeReviewerWorkload } from "@/lib/roles";

const STATS = [
  { key: "assigned_to_me", label: "Assigned to Me", params: { reviewer: "me" }, tone: "text-[#0A0F1C]" },
  { key: "re_review_required", label: "Re-Review Required", params: { status: "re_review_required" }, tone: "text-red-600" },
  { key: "overdue", label: "Overdue", params: { overdue: "true" }, tone: "text-amber-600" },
  { key: "claimable_now", label: "Claimable Now", params: { reviewer: "unassigned" }, tone: "text-emerald-700" },
];

// Compact reviewer-load strip for the staff dashboard. Reuses the SAME authorized workload
// count query (RBAC enforced server-side); shown only to legal/compliance reviewer roles.
export default function ReviewerLoadStrip({ role }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!canSeeReviewerWorkload(role)) return;
    api.get("/contracts/reviewer-workload/view-counts")
      .then(({ data }) => { setSummary(data.summary || null); setError(false); })
      .catch(() => setError(true));
  }, [role]);

  if (!canSeeReviewerWorkload(role)) return null;

  return (
    <div className="mb-6" data-testid="dashboard-reviewer-load">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <ClipboardCheck size={13} /> My review load
        </span>
        <button onClick={() => navigate("/reviewer-workload")} data-testid="dashboard-open-workload"
          className="text-[11px] font-medium text-[#2563EB] hover:underline">Open Reviewer Workload →</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {STATS.map((s) => {
          const qs = new URLSearchParams(s.params).toString();
          return (
            <button key={s.key} data-testid={`dashboard-summary-${s.key}`}
              onClick={() => navigate(`/reviewer-workload?${qs}`)}
              className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-[#2563EB] transition-colors">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{s.label}</span>
              <span className={`font-mono text-lg font-bold ${s.tone}`}>
                {error && summary === null ? "!" : summary === null ? "…" : (summary[s.key] ?? 0)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
